import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Heart, ArrowRight, MapPin, Loader2, Send, Sparkles, Quote, X, Mic, MicOff } from "lucide-react";
import QRCode from "../components/QRCode";
import CompassionScore from "../components/CompassionScore";
import HerzLevel from "../components/HerzLevel";
import HerzMap from "../components/HerzMap";
import SEO from "../components/SEO";
import HerzTV from "../components/HerzTV";

const API = process.env.REACT_APP_BACKEND_URL;

// ── HERZBEWEGUNG palette (warm, distinct from SAT midnight)
const COLORS = {
  bg: "#1A0C12",        // tiefes Weinrot-Dunkel
  panel: "#2A1218",
  gold: "#D4AF37",
  cream: "#F7EEDD",
  wine: "#7A1F2B",
  rose: "#E8B4A2",
  ink: "#1A0C12",
};

export default function Herzbewegung() {
  const [stats, setStats] = useState({ total_acts: 0, people_helped: 0, active_members: 0 });
  const [mission, setMission] = useState(null);
  const [feed, setFeed] = useState([]);
  const [showShare, setShowShare] = useState(false);
  const [amaraOpen, setAmaraOpen] = useState(false);

  const load = async () => {
    try {
      const [s, m, f] = await Promise.all([
        axios.get(`${API}/api/herz/stats`),
        axios.get(`${API}/api/herz/mission/today`),
        axios.get(`${API}/api/herz/feed?limit=12`),
      ]);
      setStats(s.data); setMission(m.data); setFeed(f.data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen text-[#F7EEDD]" style={{ background: COLORS.bg }} data-testid="herzbewegung-page">
      <SEO
        title="Herzbewegung · Für mehr Menschlichkeit"
        description="Die Herzbewegung ist eine Bewegung für mehr Menschlichkeit, Mitgefühl und Verantwortung. Werde Herzbotschafter — gemeinsam gestalten wir eine bessere Welt."
        path="/herzbewegung"
      />
      {/* HERO */}
      <section className="relative overflow-hidden pt-24 pb-20 lg:pb-28">
        <div className="absolute inset-0 pointer-events-none opacity-50"
             style={{ background: "radial-gradient(ellipse at 20% 30%, #7A1F2B 0%, transparent 55%), radial-gradient(ellipse at 80% 70%, #D4AF37 0%, transparent 55%)" }} />
        <div className="relative container-sat">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#F7EEDD]/55 hover:text-[#D4AF37] mb-6" data-testid="herz-back-sat">
            <ArrowRight size={14} className="rotate-180" /> Zurück zur SAT Akademie
          </Link>

          <div className="grid lg:grid-cols-[1.15fr_1fr] gap-12 lg:gap-20 items-center">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3 mb-5">
                <Heart size={20} className="text-[#D4AF37] fill-[#D4AF37]" />
                <div className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase">Herzbewegung</div>
              </div>
              <h1 className="serif text-5xl md:text-7xl lg:text-[5.6rem] xl:text-[6.4rem] font-light tracking-tight leading-[1.04]">
                Die Welt braucht<br/>
                <span className="italic" style={{ color: COLORS.gold }}>mehr Herz.</span>
              </h1>
              <p className="text-[#F7EEDD]/75 text-lg md:text-xl mt-8 max-w-xl leading-relaxed">
                Werde Teil einer Bewegung, die Menschlichkeit wieder sichtbar macht.<br/>
                <span className="italic text-[#D4AF37]">Geh jetzt den ersten Schritt.</span>
              </p>

              <div className="mt-10 flex flex-wrap gap-3">
                <a href="#mission" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-base hover:opacity-90 transition"
                   style={{ background: COLORS.gold, color: COLORS.ink }}
                   data-testid="herz-cta-mitmachen">
                  <Heart size={16} className="fill-current" /> Jetzt mitmachen
                </a>
                <button onClick={() => setShowShare(true)}
                        className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-base border-2 border-[#F7EEDD]/30 hover:border-[#D4AF37] transition"
                        data-testid="herz-cta-aktion">
                  Erste Herzensaktion starten <ArrowRight size={14} />
                </button>
              </div>

              {/* Live Counter */}
              <div className="mt-14 grid grid-cols-3 gap-4 lg:gap-8 max-w-3xl" data-testid="herz-counter">
                <Counter value={stats.total_acts} label="Herzensaktionen" accent={COLORS.gold} />
                <Counter value={stats.people_helped} label="Menschen geholfen" accent={COLORS.rose} />
                <Counter value={stats.active_members} label="Aktive Mitglieder" accent={COLORS.cream} />
              </div>
            </div>

            {/* Right: pulsing heart visual */}
            <div className="hidden lg:flex relative items-center justify-center min-h-[520px]">
              <HeartPulse />
            </div>
          </div>
        </div>
      </section>

      {/* SAT-HTC × Herzbewegung — Serve-Ebene */}
      <section className="py-16 border-t border-[#F7EEDD]/5" style={{ background: "#0F0810" }} data-testid="herz-htc-serve">
        <div className="container-sat max-w-4xl text-center">
          <div className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase mb-4">Die Serve-Ebene von SAT-HTC</div>
          <h2 className="serif text-3xl md:text-4xl font-light leading-tight">
            Wenn Transformation zur Handlung wird.
          </h2>
          <p className="text-[#F7EEDD]/75 mt-6 text-lg leading-relaxed max-w-2xl mx-auto">
            Die Herzbewegung ist die Frucht der SAT-Arbeit. Echte Transformation endet nicht beim
            Ich — sie wird sichtbar, wenn ein Mensch beginnt, bewusster zu lieben und zu handeln.
            Im <Link to="/sat-htc" className="text-[#D4AF37] hover:underline">SAT-HTC</Link> steht
            diese Ebene für <span className="italic text-[#D4AF37]">Serve</span>: Die eigene
            Entwicklung wird zum Beitrag.
          </p>
        </div>
      </section>

      {/* HERZKREIS */}
      <section className="py-20 border-t border-[#F7EEDD]/5" style={{ background: COLORS.panel }}>
        <div className="container-sat max-w-5xl">
          {/* Personal widgets (only logged-in) */}
          <HerzLevel />
          <CompassionScore variant="herz" />
          <div className="text-center mb-12">
            <div className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase mb-3">Der Herzkreis</div>
            <h2 className="serif text-3xl md:text-5xl font-light leading-tight">Wie eine Welle entsteht.</h2>
          </div>
          <div className="grid sm:grid-cols-5 gap-4">
            {[
              { n: 1, t: "Inspiration", d: "Eine Idee wird geboren." },
              { n: 2, t: "Gute Tat", d: "Aus Idee wird Handlung." },
              { n: 3, t: "Erfahrung teilen", d: "Worte machen sichtbar." },
              { n: 4, t: "Andere inspirieren", d: "Funke springt über." },
              { n: 5, t: "Welt verändern", d: "Eine Welle entsteht." },
            ].map((s, i) => (
              <div key={s.n} className="relative p-5 rounded-2xl text-center border" style={{ borderColor: "#D4AF37" + "30", background: "rgba(212,175,55,0.04)" }} data-testid={`herz-step-${s.n}`}>
                <div className="serif text-3xl font-light text-[#D4AF37]">{s.n}</div>
                <div className="serif text-lg mt-2">{s.t}</div>
                <div className="text-xs text-[#F7EEDD]/55 mt-1.5">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION OF THE DAY */}
      <section id="mission" className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-30"
             style={{ background: "radial-gradient(circle at 70% 30%, #D4AF37 0%, transparent 50%)" }} />
        <div className="relative max-w-4xl mx-auto px-6">
          <div className="text-center mb-8">
            <div className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase mb-3">Heutige Herzensmission</div>
            <h2 className="serif text-2xl md:text-3xl font-light text-[#F7EEDD]/75">Eine kleine Handlung kann das Leben eines Menschen verändern.</h2>
          </div>
          {mission && (
            <div className="rounded-3xl p-10 md:p-14 text-center border-2"
                 style={{ borderColor: COLORS.gold, background: `radial-gradient(ellipse at top, ${COLORS.gold}15 0%, transparent 70%), ${COLORS.panel}` }}
                 data-testid="herz-mission-today">
              <Heart size={36} className="mx-auto mb-6 text-[#D4AF37] fill-[#D4AF37]" />
              <h3 className="serif text-3xl md:text-5xl font-light leading-tight">{mission.title}</h3>
              <p className="text-[#F7EEDD]/65 text-base md:text-lg italic mt-6 max-w-2xl mx-auto leading-relaxed">{mission.body}</p>
              <button onClick={() => setShowShare(true)}
                      className="mt-10 inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-semibold transition hover:opacity-90"
                      style={{ background: COLORS.gold, color: COLORS.ink }}
                      data-testid="herz-mission-accept">
                <Heart size={16} className="fill-current" /> Ich nehme die Mission an
              </button>
            </div>
          )}
        </div>
      </section>

      {/* FEED */}
      <section className="py-20 border-t border-[#F7EEDD]/5" style={{ background: COLORS.panel }}>
        <div className="container-sat max-w-5xl">
          <div className="text-center mb-12">
            <div className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase mb-3">Herzgeschichten</div>
            <h2 className="serif text-3xl md:text-5xl font-light leading-tight">Was Menschen heute getan haben.</h2>
          </div>
          {feed.length === 0 ? (
            <div className="text-center py-16 text-[#F7EEDD]/45 italic" data-testid="herz-feed-empty">Noch keine Geschichten — sei du die erste Stimme.</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="herz-feed">
              {feed.map((a) => <ActCard key={a.id} a={a} />)}
            </div>
          )}
        </div>
      </section>

      {/* HERZKARTE — Live geocoded acts */}
      <HerzMap />

      {/* HERZ-TV — curated kindness video portal */}
      <HerzTV />

      {/* JOIN CTA + QR */}
      <section className="py-20 relative">
        <div className="max-w-4xl mx-auto px-6 grid md:grid-cols-[1fr,auto] gap-10 items-center">
          <div>
            <div className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase mb-3">Werde Teil</div>
            <h3 className="serif text-3xl md:text-4xl font-light leading-tight">
              Wenn du die Seite verlässt,<br/>nimm einen Gedanken mit:
            </h3>
            <p className="serif italic text-2xl mt-6 text-[#D4AF37]">„Ich möchte heute etwas Gutes tun."</p>
            <p className="text-[#F7EEDD]/55 text-sm mt-4">Trage die Bewegung weiter — scanne und teile mit jemandem, dem es helfen würde.</p>
          </div>
          <QRCode value={typeof window !== "undefined" ? window.location.href : "https://sat-akademie.com/herzbewegung"} size={7} label="Herzbewegung teilen" />
        </div>
      </section>

      {/* Connection to SAT */}
      <section className="py-16 border-t border-[#F7EEDD]/5 text-center" style={{ background: COLORS.panel }}>
        <div className="max-w-3xl mx-auto px-6">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="serif text-xl text-[#F7EEDD]/75">SAT</span>
            <span className="text-[#D4AF37]">×</span>
            <span className="serif text-xl text-[#D4AF37]">Herzbewegung</span>
          </div>
          <p className="text-[#F7EEDD]/55 leading-relaxed">
            <span className="italic">SAT</span> erklärt die Spielregeln des Lebens. <span className="italic" style={{ color: COLORS.gold }}>Herzbewegung</span> bringt sie auf die Straße.
          </p>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 text-sm text-[#F7EEDD]/65 hover:text-[#D4AF37]">
            Zur SAT Akademie <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* AMARA Floating Button */}
      {!amaraOpen && (
        <button onClick={() => setAmaraOpen(true)}
                className="fixed bottom-24 left-6 z-[110] inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold shadow-2xl transition-transform hover:scale-105"
                style={{ background: `linear-gradient(135deg, ${COLORS.wine} 0%, ${COLORS.gold} 100%)`, color: COLORS.cream }}
                data-testid="amara-toggle">
          <Heart size={16} className="fill-current" />
          <span className="text-sm">AMARA</span>
          <span className="ml-1 w-2 h-2 rounded-full bg-[#F7EEDD] animate-pulse" />
        </button>
      )}

      {amaraOpen && <AmaraChat onClose={() => setAmaraOpen(false)} />}

      {/* Share / Act Submit Modal */}
      {showShare && <SubmitActModal onClose={() => setShowShare(false)} onSubmitted={() => { setShowShare(false); load(); }} mission={mission} />}
    </div>
  );
}

// ── COMPONENTS ─────────────────────────────────────────────────────
const Counter = ({ value, label, accent }) => (
  <div className="text-left">
    <div className="serif text-5xl md:text-6xl font-light" style={{ color: accent }}>
      <Heart size={20} className="inline-block mr-2 align-middle fill-current opacity-80" />
      {(value ?? 0).toLocaleString("de-CH")}
    </div>
    <div className="text-[10px] uppercase tracking-[0.3em] text-[#F7EEDD]/45 mt-1">{label}</div>
  </div>
);

// HeartPulse — pulsing heart sphere with orbiting compassion sparks
const HeartPulse = () => (
  <div className="relative w-full max-w-[500px] mx-auto aspect-square" data-testid="herz-heart-pulse">
    <svg viewBox="-260 -260 520 520" className="w-full h-full" style={{ overflow: "visible" }}>
      <defs>
        <radialGradient id="hp-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.32" />
          <stop offset="45%" stopColor="#7A1F2B" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#1A0C12" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="hp-heart" cx="50%" cy="40%" r="60%">
          <stop offset="0%" stopColor="#F2D6D6" stopOpacity="1" />
          <stop offset="50%" stopColor="#E8B4A2" stopOpacity="1" />
          <stop offset="100%" stopColor="#7A1F2B" stopOpacity="1" />
        </radialGradient>
        <filter id="hp-blur" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" />
        </filter>
      </defs>
      {/* outer halo */}
      <circle r="240" fill="url(#hp-glow)" />
      {/* orbital rings */}
      <circle r="200" fill="none" stroke="rgba(212,175,55,0.22)" strokeWidth="0.7" strokeDasharray="2 6" />
      <circle r="150" fill="none" stroke="rgba(232,180,162,0.18)" strokeWidth="0.6" />
      <circle r="100" fill="none" stroke="rgba(212,175,55,0.16)" strokeWidth="0.5" strokeDasharray="3 5" />
      {/* center heart shape */}
      <g style={{ animation: "hp-beat 1.6s ease-in-out infinite", transformOrigin: "0 0" }}>
        <path d="M 0 50 C -60 -20, -110 -20, -110 -60 C -110 -110, -60 -130, 0 -70 C 60 -130, 110 -110, 110 -60 C 110 -20, 60 -20, 0 50 Z"
              fill="url(#hp-heart)"
              filter="url(#hp-blur)"
              opacity="0.85" />
        <path d="M 0 50 C -60 -20, -110 -20, -110 -60 C -110 -110, -60 -130, 0 -70 C 60 -130, 110 -110, 110 -60 C 110 -20, 60 -20, 0 50 Z"
              fill="none"
              stroke="#D4AF37"
              strokeWidth="1.5"
              opacity="0.6" />
      </g>
      {/* 12 orbiting compassion sparks */}
      {Array.from({ length: 12 }).map((_, i) => {
        const a = (i / 12) * Math.PI * 2;
        const orbitR = i % 2 === 0 ? 200 : 150;
        return (
          <g key={i} style={{
            animation: `hp-orbit ${20 + i % 5}s linear infinite`,
            animationDelay: `${-i * 1.5}s`,
            transformOrigin: "0 0"
          }}>
            <circle cx={Math.cos(a) * orbitR} cy={Math.sin(a) * orbitR}
                    r="3.5" fill="#D4AF37" />
            <circle cx={Math.cos(a) * orbitR} cy={Math.sin(a) * orbitR}
                    r="7" fill="#D4AF37" opacity="0.25" />
          </g>
        );
      })}
    </svg>
    <style>{`
      @keyframes hp-beat {
        0%, 100% { transform: scale(0.96); }
        15%      { transform: scale(1.04); }
        30%      { transform: scale(0.98); }
        45%      { transform: scale(1.06); }
        60%      { transform: scale(1.00); }
      }
      @keyframes hp-orbit {
        from { transform: rotate(0deg); }
        to   { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);


const ActCard = ({ a }) => {
  const initials = (a.actor_name || "A").split(" ").map(s => s[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div className="p-5 rounded-2xl border border-[#F7EEDD]/10 bg-[#1A0C12]/40 hover:border-[#D4AF37]/40 transition" data-testid={`herz-act-${a.id}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm" style={{ background: "#D4AF37", color: "#1A0C12" }}>{initials}</div>
        <div>
          <div className="text-sm text-[#F7EEDD]/85 font-medium">{a.actor_name}</div>
          {a.location && <div className="text-[10px] text-[#F7EEDD]/45 inline-flex items-center gap-1"><MapPin size={10} /> {a.location}</div>}
        </div>
      </div>
      <p className="text-sm text-[#F7EEDD]/75 leading-relaxed">{a.text}</p>
    </div>
  );
};

// ── Submit Act Modal
const SubmitActModal = ({ onClose, onSubmitted, mission }) => {
  const [text, setText] = useState("");
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (text.trim().length < 10) { alert("Bitte beschreibe deine Tat in mindestens 10 Zeichen."); return; }
    setBusy(true);
    try {
      const token = localStorage.getItem("sat_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/api/herz/act`, {
        text: text.trim(), actor_name: name.trim() || "Anonym",
        location: location.trim() || null,
        mission_id: mission?.id,
      }, { headers });
      onSubmitted();
    } catch (e) {
      alert(e?.response?.data?.detail || "Konnte nicht gespeichert werden.");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="herz-submit-modal">
      <div className="rounded-2xl p-6 md:p-8 w-full max-w-md border" style={{ background: "#2A1218", borderColor: "#D4AF3740" }}>
        <div className="flex items-center justify-between mb-4">
          <div className="serif text-2xl text-[#D4AF37]">Deine Herzensaktion</div>
          <button onClick={onClose} className="text-[#F7EEDD]/55 hover:text-[#F7EEDD]" data-testid="herz-submit-close"><X size={18} /></button>
        </div>
        <p className="text-[#F7EEDD]/55 text-xs mb-4">Erzähl uns davon. Nicht für Selbstdarstellung — für Inspiration.</p>
        <textarea value={text} onChange={(e) => setText(e.target.value)}
                  placeholder="Was hast du heute aus dem Herzen heraus getan?"
                  rows={4} className="w-full bg-black/30 border border-[#F7EEDD]/10 rounded-lg p-3 text-sm focus:border-[#D4AF37] focus:outline-none text-[#F7EEDD]"
                  data-testid="herz-submit-text" />
        <div className="grid grid-cols-2 gap-3 mt-3">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Dein Vorname (optional)"
                 className="bg-black/30 border border-[#F7EEDD]/10 rounded-lg p-3 text-sm focus:border-[#D4AF37] focus:outline-none text-[#F7EEDD]"
                 data-testid="herz-submit-name" />
          <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Ort (z. B. Zürich)"
                 className="bg-black/30 border border-[#F7EEDD]/10 rounded-lg p-3 text-sm focus:border-[#D4AF37] focus:outline-none text-[#F7EEDD]"
                 data-testid="herz-submit-location" />
        </div>
        <button onClick={submit} disabled={busy}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold hover:opacity-90 disabled:opacity-50 transition"
                style={{ background: "#D4AF37", color: "#1A0C12" }}
                data-testid="herz-submit-go">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <><Heart size={14} className="fill-current" /> Teilen</>}
        </button>
        <p className="text-[10px] text-[#F7EEDD]/40 mt-3 text-center italic">„Nicht reden. Tun. Dann erzählen."</p>
      </div>
    </div>
  );
};

// ── AMARA Chat (with voice input + auth memory)
const AmaraChat = ({ onClose }) => {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sid, setSid] = useState(null);
  const [listening, setListening] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const ref = useRef(null);
  const recogRef = useRef(null);

  useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [msgs, busy]);

  // Init Web Speech API (browser-native, no key needed)
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    setVoiceSupported(true);
    const recog = new SR();
    recog.lang = "de-DE";
    recog.continuous = false;
    recog.interimResults = true;
    recog.onresult = (ev) => {
      let transcript = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        transcript += ev.results[i][0].transcript;
      }
      setInput(transcript);
    };
    recog.onend = () => setListening(false);
    recog.onerror = () => setListening(false);
    recogRef.current = recog;
    return () => { try { recog.stop(); } catch (_) { /* noop */ } };
  }, []);

  const toggleVoice = () => {
    if (!recogRef.current) return;
    if (listening) {
      try { recogRef.current.stop(); } catch (_) { /* noop */ }
      setListening(false);
    } else {
      try {
        setInput("");
        recogRef.current.start();
        setListening(true);
      } catch (_) {
        setListening(false);
      }
    }
  };

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t || busy) return;
    if (listening) { try { recogRef.current.stop(); } catch (_) { /* noop */ } setListening(false); }
    setInput(""); setMsgs((m) => [...m, { role: "user", content: t }]); setBusy(true);
    try {
      // Pass auth token if available so AMARA can use personal memory
      const token = localStorage.getItem("sat_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const r = await axios.post(`${API}/api/herz/amara`, { message: t, session_id: sid }, { headers });
      setSid(r.data.session_id);
      setMsgs((m) => [...m, { role: "assistant", content: r.data.reply }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", content: "Bitte einen Moment Geduld — ich bin wieder da." }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed bottom-24 left-6 z-[120] w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-7rem)] rounded-2xl overflow-hidden flex flex-col shadow-2xl"
         style={{ background: "linear-gradient(180deg,#2A1218 0%,#1A0C12 100%)", border: "1px solid #D4AF3740" }}
         data-testid="amara-panel">
      <div className="flex items-center justify-between p-4 border-b border-[#F7EEDD]/10">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "radial-gradient(circle,#D4AF37 0%,#7A1F2B 100%)" }}>
            <Heart size={14} className="text-[#F7EEDD] fill-[#F7EEDD]" />
          </div>
          <div>
            <div className="serif text-base text-[#F7EEDD]">AMARA</div>
            <div className="text-[10px] uppercase tracking-wider text-[#F7EEDD]/55">KI-Herzcoach · online</div>
          </div>
        </div>
        <button onClick={onClose} className="text-[#F7EEDD]/55 hover:text-[#F7EEDD]" data-testid="amara-close"><X size={18} /></button>
      </div>
      <div ref={ref} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="amara-messages">
        {msgs.length === 0 && (
          <div className="space-y-3">
            <div className="bg-white/[0.04] border border-[#F7EEDD]/10 rounded-lg p-4 text-sm leading-relaxed text-[#F7EEDD]/85">
              Hallo. Ich bin <b className="text-[#D4AF37]">AMARA</b>. Frag mich nach einer kleinen Herzenshandlung für heute — oder erzähl, was dich gerade bewegt.
              {voiceSupported && <div className="text-[11px] text-[#F7EEDD]/55 mt-2 italic">Tipp: Drücke das Mikrofon-Symbol, um zu sprechen.</div>}
            </div>
            {["Was kann ich heute Gutes tun?", "Ich weiß nicht, wo ich anfangen soll.", "Erzähl mir die heutige Mission."].map(s => (
              <button key={s} onClick={() => send(s)} className="block w-full text-left px-3 py-2 text-sm bg-white/[0.03] hover:bg-white/[0.08] border border-[#F7EEDD]/10 rounded-lg text-[#F7EEDD]/75 hover:text-[#F7EEDD] transition" data-testid={`amara-starter-${s.slice(0,10)}`}>→ {s}</button>
            ))}
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
              m.role === "user" ? "bg-[#D4AF37] text-[#1A0C12] rounded-br-sm" : "bg-white/[0.06] text-[#F7EEDD]/90 border border-[#F7EEDD]/10 rounded-bl-sm"
            }`}>{m.content}</div>
          </div>
        ))}
        {busy && <div className="flex justify-start"><div className="bg-white/[0.06] border border-[#F7EEDD]/10 rounded-2xl px-4 py-3 text-[#F7EEDD]/60"><Loader2 size={14} className="animate-spin" /></div></div>}
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-[#F7EEDD]/10 p-3 flex items-end gap-2">
        {voiceSupported && (
          <button type="button" onClick={toggleVoice}
                  className={`w-10 h-10 flex items-center justify-center rounded-full border transition ${
                    listening ? "bg-[#7A1F2B] border-[#7A1F2B] text-[#F7EEDD] animate-pulse" : "bg-white/[0.05] border-[#F7EEDD]/15 text-[#F7EEDD]/70 hover:text-[#D4AF37] hover:border-[#D4AF37]/40"
                  }`}
                  data-testid="amara-voice"
                  title={listening ? "Aufnahme stoppen" : "Spracheingabe starten"}>
            {listening ? <MicOff size={14} /> : <Mic size={14} />}
          </button>
        )}
        <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={1}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder={listening ? "Ich höre dir zu…" : "Schreib eine Nachricht…"}
                  className="flex-1 bg-white/[0.05] border border-[#F7EEDD]/10 rounded-lg px-3 py-2 text-sm text-[#F7EEDD] placeholder-[#F7EEDD]/30 focus:border-[#D4AF37] focus:outline-none resize-none max-h-32"
                  data-testid="amara-input" />
        <button type="submit" disabled={busy || !input.trim()} className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D4AF37] text-[#1A0C12] disabled:opacity-40 hover:opacity-90 transition" data-testid="amara-send">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        </button>
      </form>
    </div>
  );
};
